import type { ProgramBlock, ProgramDay, TourEvent } from './event.types';

export const LOCALE = 'es-ES';

/* =============================================================================
 * FECHAS Y HORAS · el sitio habla espanol y cada destino tiene su huso.
 * -----------------------------------------------------------------------------
 * TRAMPA QUE SE EVITA AQUI (bug clasico de perdida silenciosa de datos):
 * `new Date('2027-01-23')` es medianoche UTC. Formateada en Buenos Aires (UTC-3)
 * imprime "22 de enero". Por eso TODA etiqueta de calendario se construye al
 * MEDIODIA UTC y se formatea en UTC: ningun huso del mundo puede correr el dia.
 * El huso del evento se usa SOLO donde toca un instante real (la cuenta atras).
 * ========================================================================== */

/** Fecha ISO (YYYY-MM-DD) -> Date al mediodia UTC. Inmune a saltos de dia. */
function isoToSafeDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Fecha ISO invalida: "${iso}"`);
  }
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** "sábado" — derivado de la fecha, nunca tecleado a mano. */
export function formatWeekday(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, { weekday: 'long', timeZone: 'UTC' }).format(isoToSafeDate(iso));
}

/** "23 de enero de 2027" */
export function formatLongDate(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(isoToSafeDate(iso));
}

/** "Sábado 23 de enero de 2027" */
export function formatFullDate(iso: string): string {
  return `${capitalize(formatWeekday(iso))} ${formatLongDate(iso)}`;
}

/** "23-24 de enero de 2027" (y cruza mes si hiciera falta). */
export function formatDateRangeLong(event: TourEvent): string {
  const start = isoToSafeDate(event.startDate);
  const end = isoToSafeDate(event.endDate);
  const month = (d: Date) => new Intl.DateTimeFormat(LOCALE, { month: 'long', timeZone: 'UTC' }).format(d);
  const year = end.getUTCFullYear();

  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${start.getUTCDate()}-${end.getUTCDate()} de ${month(start)} de ${year}`;
  }
  return `${start.getUTCDate()} de ${month(start)} - ${end.getUTCDate()} de ${month(end)} de ${year}`;
}

/** "23 - 24 ENERO 2027" — la fecha del hero de cada ciudad, como el mockup. */
export function formatDateRangeHero(event: TourEvent): string {
  const start = isoToSafeDate(event.startDate);
  const end = isoToSafeDate(event.endDate);
  const month = (d: Date) =>
    new Intl.DateTimeFormat(LOCALE, { month: 'long', timeZone: 'UTC' }).format(d).toUpperCase();
  const year = end.getUTCFullYear();

  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${start.getUTCDate()} - ${end.getUTCDate()} ${month(start)} ${year}`;
  }
  return `${start.getUTCDate()} ${month(start)} - ${end.getUTCDate()} ${month(end)} ${year}`;
}

/** "DÍA 1" / "DÍA 2". Se deriva del dato: nadie lo teclea en una pagina. */
export function formatDayLabel(day: ProgramDay): string {
  return `DÍA ${day.day}`;
}

/** "SÁBADO" — dia de la semana en mayusculas, derivado de la fecha. */
export function formatWeekdayUpper(iso: string): string {
  return formatWeekday(iso).toUpperCase();
}

/** true cuando el dia tiene un unico bloque (el domingo de las 4 ciudades). */
export function isSingleBlockDay(day: ProgramDay): boolean {
  return day.blocks.length === 1;
}

/** Los "hitos" que el mockup destaca de un dia, en orden cronologico. */
export function getDayHighlights(day: ProgramDay): readonly ProgramBlock[] {
  return day.blocks.filter((block) => block.isHighlight);
}

/** "23-24 ENE" — la etiqueta corta de las tarjetas del hub. */
export function formatDateRangeShort(event: TourEvent): string {
  const start = isoToSafeDate(event.startDate);
  const end = isoToSafeDate(event.endDate);
  const short = (d: Date) =>
    new Intl.DateTimeFormat(LOCALE, { month: 'short', timeZone: 'UTC' })
      .format(d)
      .replace('.', '')
      .toUpperCase();

  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${start.getUTCDate()}-${end.getUTCDate()} ${short(start)}`;
  }
  return `${start.getUTCDate()} ${short(start)} - ${end.getUTCDate()} ${short(end)}`;
}

/** `<time datetime="...">` de un rango: siempre la fecha de inicio en ISO. */
export function machineDate(event: TourEvent): string {
  return event.startDate;
}

/** "10:00 - 12:00", o "Desde las 22:00" cuando el bloque queda abierto. */
export function formatBlockTime(block: ProgramBlock): string {
  return block.end === null ? `Desde las ${block.start}` : `${block.start} - ${block.end}`;
}

/* ------------------------------------------------------------------------- */

/**
 * Desfase de un huso respecto a UTC en un instante dado, en milisegundos.
 * Se calcula con `Intl` (la unica base de datos de husos fiable del navegador)
 * en vez de a ojo: Argentina no tiene DST, Miami si, y Mexico lo abolio en 2022.
 */
function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`Intl no devolvio "${type}" para el huso "${timeZone}"`);
    return Number(part.value);
  };

  // Algunos motores devuelven "24" para la medianoche: se normaliza.
  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second'),
  );
  return asIfUtc - instant.getTime();
}

/**
 * "2027-01-23 10:00 en America/Argentina/Buenos_Aires" -> instante UTC real.
 * Doble pasada para clavar el desfase tambien en los saltos de horario de verano.
 */
export function zonedDateTimeToInstant(isoDate: string, time: string, timeZone: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined ||
    [year, month, day, hour, minute].some(Number.isNaN)
  ) {
    throw new Error(`Fecha/hora invalida: "${isoDate}" "${time}"`);
  }
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const firstPass = naive - timeZoneOffsetMs(new Date(naive), timeZone);
  const secondPass = naive - timeZoneOffsetMs(new Date(firstPass), timeZone);
  return new Date(secondPass);
}

/**
 * CUANTOS DIAS DURA el destino. Los cuenta el programa cuando lo hay y, cuando
 * todavia no se ha publicado (Funes, 23-sep-2026), sus FECHAS — que el cliente
 * si ha dado. Vive AQUI, en el dominio, y no en la pagina que lo pinta: es la
 * misma regla que aplican la cuenta atras y el JSON-LD, y repetida en tres
 * ficheros se descuadra en cuanto uno de los tres cambie.
 */
export function getEventDayCount(event: TourEvent): number {
  if (event.program.length > 0) return event.program.length;
  const DIA_MS = 24 * 60 * 60 * 1000;
  return Math.round((Date.parse(event.endDate) - Date.parse(event.startDate)) / DIA_MS) + 1;
}

/**
 * Instante en que ARRANCA el evento: primer bloque del dia 1, en su huso.
 * Es el objetivo de `Countdown`. Es un dato objetivo, no escasez inventada
 * (ADR-003 §4: cuenta atras SI, "plazas restantes" NO).
 */
export function getEventStartInstant(event: TourEvent): Date {
  const firstDay = event.program[0];
  const firstBlock = firstDay?.blocks[0];
  /* SIN PROGRAMA PUBLICADO (Funes, 23-sep-2026) la cuenta atras apunta al
   * comienzo del primer DIA en el huso del destino. No es una hora inventada:
   * es el unico dato que el cliente ha dado —la fecha— leido a su valor
   * minimo. En cuanto llegue el horario, el primer bloque manda solo. */
  if (!firstDay || !firstBlock) {
    return zonedDateTimeToInstant(event.startDate, '00:00', event.timeZone);
  }
  return zonedDateTimeToInstant(firstDay.date, firstBlock.start, event.timeZone);
}
